import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import AnimalPhotoGallery from './AnimalPhotoGallery';
import { todayLocal } from '../../utils/dates';
import { validateDate, validateQuantity, maxWeightForUnit } from '../../utils/validation';

// Extract FastAPI error detail (string, array, or object) into a printable string.
const trackerErrMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join(', ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || fallback;
};
import { 
  Star, Plus, Edit2, Trash2, Calendar, Scale, Heart, 
  Egg, Milk, ClipboardList, ChevronRight, X, Camera, Tag, Upload, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GENDER_OPTIONS = [
  { value: 'female', label: 'Female', symbol: '♀', color: 'text-pink-500' },
  { value: 'male', label: 'Male', symbol: '♂', color: 'text-blue-500' },
  { value: 'unknown', label: 'Unknown', symbol: '?', color: 'text-gray-500' }
];

const EVENT_TYPES = [
  { value: 'health', label: 'Health Check', icon: Heart, color: 'bg-red-100 text-red-700' },
  { value: 'weight', label: 'Weight', icon: Scale, color: 'bg-blue-100 text-blue-700' },
  { value: 'production', label: 'Production', icon: Egg, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'breeding', label: 'Breeding', icon: Heart, color: 'bg-pink-100 text-pink-700' },
  { value: 'note', label: 'Note', icon: ClipboardList, color: 'bg-gray-100 text-gray-700' }
];

const IndividualAnimalTracker = ({ 
  animalType, // "poultry", "goat", "sheep", "cattle"
  groupId,    // flock_id or herd_id to filter by
  groupName,  // name of the flock/herd for display
  getAuthHeader,
  breeds = [] // available breeds for this animal type
}) => {
  const [animals, setAnimals] = useState([]);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  
  // Form states
  const [animalForm, setAnimalForm] = useState({
    name: '',
    breed: '',
    gender: 'unknown',
    birth_date: '',
    tag_number: '',
    photo_url: '',
    notes: ''
  });
  
  const [eventForm, setEventForm] = useState({
    event_type: 'note',
    description: '',
    value: '',
    unit: '',
    date: todayLocal(),
    notes: ''
  });

  useEffect(() => {
    fetchAnimals();
  }, [animalType, groupId]);

  // Check for deep link to open specific animal (from within this component)
  useEffect(() => {
    const checkForDeepLink = () => {
      const openAnimalId = localStorage.getItem('openIndividualAnimal');
      if (openAnimalId && animals.length > 0) {
        localStorage.removeItem('openIndividualAnimal');
        // Check if this animal is in our current list
        const animal = animals.find(a => a.id === openAnimalId);
        if (animal) {
          fetchAnimalDetail(openAnimalId);
          return true;
        }
      }
      return false;
    };
    
    // Check immediately and with a small delay
    if (checkForDeepLink()) return;
    
    const timer = setTimeout(checkForDeepLink, 300);
    return () => clearTimeout(timer);
  }, [animals]);

  const fetchAnimals = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/animals/individual?animal_type=${animalType}`;
      if (groupId) {
        url += `&group_id=${groupId}`;
      }
      const res = await axios.get(url, { headers: getAuthHeader() });
      setAnimals(res.data.animals || []);
    } catch (error) {
      console.error('Failed to fetch individual animals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnimalDetail = async (animalId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/individual/${animalId}`,
        { headers: getAuthHeader() }
      );
      setSelectedAnimal(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Failed to load animal details:', error.response?.status, error.response?.data);
      // Show more helpful error message
      if (error.response?.status === 401) {
        toast.error('Session expired. Please refresh the page.');
      } else if (error.response?.status === 404) {
        toast.error('Animal not found. It may have been deleted.');
        fetchAnimals(); // Refresh the list
      } else {
        toast.error('Failed to load animal details. Please try again.');
      }
    }
  };

  const handleCreateAnimal = async () => {
    const breedName = animalForm.breed === '__custom__' ? animalForm.customBreed : animalForm.breed;
    if (!animalForm.name) {
      toast.error('Please enter a name');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/animals/individual`, {
        ...animalForm,
        breed: breedName || '',
        animal_type: animalType,
        group_id: groupId,
        is_prized: true
      }, { headers: getAuthHeader() });
      
      toast.success('Animal added!');
      setShowAddDialog(false);
      resetAnimalForm();
      fetchAnimals();
    } catch (error) {
      toast.error('Failed to add animal');
    }
  };

  const handleUpdateAnimal = async () => {
    const breedName = animalForm.breed === '__custom__' ? animalForm.customBreed : animalForm.breed;
    try {
      await axios.put(
        `${API_URL}/api/animals/individual/${editingAnimal.id}`,
        {
          ...animalForm,
          breed: breedName || ''
        },
        { headers: getAuthHeader() }
      );
      toast.success('Animal updated!');
      setShowAddDialog(false);
      setEditingAnimal(null);
      resetAnimalForm();
      fetchAnimals();
      if (selectedAnimal?.id === editingAnimal.id) {
        fetchAnimalDetail(editingAnimal.id);
      }
    } catch (error) {
      toast.error('Failed to update animal');
    }
  };

  const handleDeleteAnimal = async (animalId) => {
    if (!window.confirm('Delete this animal? This will also delete all associated records.')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/animals/individual/${animalId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Animal deleted');
      setShowDetailDialog(false);
      setSelectedAnimal(null);
      fetchAnimals();
    } catch (error) {
      toast.error('Failed to delete animal');
    }
  };

  // Toggle Foundation Star status
  const handleToggleFoundation = async (animal) => {
    const newFoundationStatus = !(animal.is_foundation === true || animal.is_foundation === undefined);
    try {
      await axios.put(
        `${API_URL}/api/animals/individual/${animal.id}`,
        { is_foundation: newFoundationStatus },
        { headers: getAuthHeader() }
      );
      toast.success(newFoundationStatus 
        ? `${animal.name} added to Foundation Animals` 
        : `${animal.name} removed from Foundation Animals`
      );
      fetchAnimals();
      // Update selected animal if viewing
      if (selectedAnimal?.id === animal.id) {
        setSelectedAnimal(prev => ({ ...prev, is_foundation: newFoundationStatus }));
      }
    } catch (error) {
      toast.error('Failed to update animal');
    }
  };

  const handleAddEvent = async () => {
    if (!eventForm.description) {
      toast.error('Please enter a description');
      return;
    }
    const etype = (eventForm.event_type || '').toLowerCase();
    if (etype === 'weight') {
      const q = validateQuantity(eventForm.value, { field: 'Weight', max: maxWeightForUnit(eventForm.unit) });
      if (!q.ok) { toast.error(q.message); return; }
    } else if (etype === 'milk') {
      const q = validateQuantity(eventForm.value, { field: 'Quantity', max: 50 });
      if (!q.ok) { toast.error(q.message); return; }
    }
    const dv = validateDate(eventForm.date, { field: 'Date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }

    try {
      await axios.post(
        `${API_URL}/api/animals/individual/${selectedAnimal.id}/events`,
        { ...eventForm, allow_historical: !dv.ok && dv.reason === 'too_old' },
        { headers: getAuthHeader() }
      );
      toast.success('Event logged!');
      setShowEventDialog(false);
      resetEventForm();
      fetchAnimalDetail(selectedAnimal.id);
    } catch (error) {
      toast.error(trackerErrMsg(error, 'Failed to log event'));
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(
        `${API_URL}/api/animals/events/${eventId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Event deleted');
      fetchAnimalDetail(selectedAnimal.id);
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const resetAnimalForm = () => {
    setAnimalForm({
      name: '',
      breed: '',
      customBreed: '',
      gender: 'unknown',
      birth_date: '',
      tag_number: '',
      photo_url: '',
      notes: ''
    });
  };

  const resetEventForm = () => {
    setEventForm({
      event_type: 'note',
      description: '',
      value: '',
      unit: '',
      date: todayLocal(),
      notes: ''
    });
  };

  const openEditDialog = (animal) => {
    setEditingAnimal(animal);
    // Check if the breed is a custom one (not in the breeds list)
    const isCustomBreed = animal.breed && breeds.length > 0 && !breeds.includes(animal.breed);
    setAnimalForm({
      name: animal.name,
      breed: isCustomBreed ? '__custom__' : (animal.breed || ''),
      customBreed: isCustomBreed ? animal.breed : '',
      gender: animal.gender || 'unknown',
      birth_date: animal.birth_date || '',
      tag_number: animal.tag_number || '',
      photo_url: animal.photo_url || '',
      notes: animal.notes || ''
    });
    setShowAddDialog(true);
  };

  const getGenderDisplay = (gender) => {
    const option = GENDER_OPTIONS.find(g => g.value === gender);
    return option ? (
      <span className={option.color}>{option.symbol}</span>
    ) : null;
  };

  const getEventIcon = (eventType) => {
    const et = EVENT_TYPES.find(e => e.value === eventType);
    return et ? et.icon : ClipboardList;
  };

  const getEventColor = (eventType) => {
    const et = EVENT_TYPES.find(e => e.value === eventType);
    return et ? et.color : 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          <h3 className="font-heading font-semibold">Foundation Animals</h3>
          {groupName && (
            <span className="text-sm text-muted-foreground">in {groupName}</span>
          )}
        </div>
        <Button 
          size="sm"
          onClick={() => { resetAnimalForm(); setEditingAnimal(null); setShowAddDialog(true); }}
          data-testid="add-individual-animal-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Animal List */}
      {animals.length === 0 ? (
        <Card className="card-farm">
          <CardContent className="py-8 text-center">
            <Star className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-3">
              No foundation animals tracked yet
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Track individual animals for detailed health, weight, and production records.
            </p>
            <Button 
              variant="outline" 
              onClick={() => setShowAddDialog(true)}
              data-testid="add-first-animal-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Animal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {animals.map((animal) => (
            <Card 
              key={animal.id}
              className="card-farm cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fetchAnimalDetail(animal.id)}
              data-testid={`individual-animal-${animal.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {animal.photo_url ? (
                    <img 
                      src={animal.photo_url} 
                      alt={animal.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-amber-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
                      <Star className="w-5 h-5 text-amber-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium truncate">{animal.name}</h4>
                      {getGenderDisplay(animal.gender)}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {animal.breed || 'Unknown breed'}
                      {animal.tag_number && ` • #${animal.tag_number}`}
                    </p>
                    {animal.status !== 'active' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                        {animal.status}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFoundation(animal);
                    }}
                    className={`p-1.5 rounded-full transition-colors ${
                      animal.is_foundation === true || animal.is_foundation === undefined
                        ? 'text-amber-500 hover:bg-amber-500/10' 
                        : 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/10'
                    }`}
                    title={animal.is_foundation === true || animal.is_foundation === undefined ? 'Remove from Foundation' : 'Add to Foundation'}
                    data-testid={`foundation-star-${animal.id}`}
                  >
                    <Star className={`w-5 h-5 ${animal.is_foundation === true || animal.is_foundation === undefined ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Animal Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              {editingAnimal ? 'Edit Animal' : 'Add Prized Animal'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Name *</Label>
              <Input
                value={animalForm.name}
                onChange={(e) => setAnimalForm({ ...animalForm, name: e.target.value })}
                placeholder="e.g., Daisy, Champion"
                className="h-9"
                data-testid="animal-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Breed</Label>
                {breeds.length > 0 ? (
                  <>
                    <select
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                      value={animalForm.breed === '__custom__' ? '__custom__' : (breeds.includes(animalForm.breed) ? animalForm.breed : (animalForm.breed ? '__custom__' : ''))}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setAnimalForm({ ...animalForm, breed: '__custom__', customBreed: '' });
                        } else {
                          setAnimalForm({ ...animalForm, breed: e.target.value, customBreed: '' });
                        }
                      }}
                      data-testid="animal-breed-select"
                    >
                      <option value="">Select...</option>
                      {breeds.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                      <option value="__custom__">✨ Custom / Hybrid</option>
                    </select>
                    {(animalForm.breed === '__custom__' || (animalForm.breed && !breeds.includes(animalForm.breed))) && (
                      <Input
                        className="mt-1 h-8"
                        placeholder="Custom breed name"
                        value={animalForm.customBreed || (animalForm.breed !== '__custom__' ? animalForm.breed : '')}
                        onChange={(e) => setAnimalForm({ ...animalForm, customBreed: e.target.value, breed: '__custom__' })}
                        data-testid="animal-custom-breed-input"
                      />
                    )}
                  </>
                ) : (
                  <Input
                    value={animalForm.breed}
                    onChange={(e) => setAnimalForm({ ...animalForm, breed: e.target.value })}
                    placeholder="Breed name"
                    className="h-9"
                    data-testid="animal-breed-input"
                  />
                )}
              </div>
              <div>
                <Label className="text-sm">Gender</Label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={animalForm.gender}
                  onChange={(e) => setAnimalForm({ ...animalForm, gender: e.target.value })}
                  data-testid="animal-gender-select"
                >
                  {GENDER_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.symbol} {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Birth Date</Label>
                <Input
                  type="date"
                  value={animalForm.birth_date}
                  onChange={(e) => setAnimalForm({ ...animalForm, birth_date: e.target.value })}
                  className="h-9"
                  data-testid="animal-birth-date-input"
                />
              </div>
              <div>
                <Label className="text-sm">Tag/ID #</Label>
                <Input
                  value={animalForm.tag_number}
                  onChange={(e) => setAnimalForm({ ...animalForm, tag_number: e.target.value })}
                  placeholder="e.g., 001"
                  className="h-9"
                  data-testid="animal-tag-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Photo (Optional)
              </Label>
              {animalForm.photo_url ? (
                <div className="relative rounded-lg overflow-hidden border mt-2">
                  <img 
                    src={animalForm.photo_url} 
                    alt="Animal preview" 
                    className="w-full h-32 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <button
                    onClick={() => setAnimalForm({ ...animalForm, photo_url: '' })}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label 
                  htmlFor="individual-animal-photo" 
                  className="flex flex-col items-center justify-center w-full h-24 mt-2 border-2 border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors active:bg-muted/30"
                  onClick={() => document.getElementById('individual-animal-photo')?.click()}
                >
                  <input
                    id="individual-animal-photo"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        toast.error('Please select an image');
                        return;
                      }
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('category', 'poultry');
                      try {
                        const res = await axios.post(`${API_URL}/api/upload/photo`, formData, {
                          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                        });
                        setAnimalForm({ ...animalForm, photo_url: res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}` });
                        toast.success('Photo uploaded!');
                      } catch (err) {
                        toast.error('Upload failed');
                      }
                    }}
                    className="sr-only"
                    data-testid="animal-photo-input"
                  />
                  <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Tap to add photo</span>
                </label>
              )}
            </div>

            <div>
              <Label className="text-sm">Notes</Label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background resize-none text-sm"
                value={animalForm.notes}
                onChange={(e) => setAnimalForm({ ...animalForm, notes: e.target.value })}
                placeholder="Special traits, history, etc."
                data-testid="animal-notes-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowAddDialog(false); setEditingAnimal(null); resetAnimalForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={editingAnimal ? handleUpdateAnimal : handleCreateAnimal}
                disabled={!animalForm.name}
                data-testid="save-animal-btn"
              >
                {editingAnimal ? 'Update' : 'Add'} Animal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Animal Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedAnimal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {selectedAnimal.photo_url ? (
                    <img 
                      src={selectedAnimal.photo_url} 
                      alt={selectedAnimal.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <Star className="w-6 h-6 text-amber-500" />
                  )}
                  {selectedAnimal.name}
                  {getGenderDisplay(selectedAnimal.gender)}
                  <button
                    onClick={() => handleToggleFoundation(selectedAnimal)}
                    className={`ml-auto p-1.5 rounded-full transition-colors ${
                      selectedAnimal.is_foundation === true || selectedAnimal.is_foundation === undefined
                        ? 'text-amber-500 hover:bg-amber-500/10' 
                        : 'text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/10'
                    }`}
                    title={selectedAnimal.is_foundation === true || selectedAnimal.is_foundation === undefined ? 'Remove from Foundation' : 'Add to Foundation'}
                    data-testid="detail-foundation-star"
                  >
                    <Star className={`w-5 h-5 ${selectedAnimal.is_foundation === true || selectedAnimal.is_foundation === undefined ? 'fill-current' : ''}`} />
                  </button>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-2">
                {/* Animal Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Breed</p>
                    <p className="font-medium">{selectedAnimal.breed || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tag #</p>
                    <p className="font-medium">{selectedAnimal.tag_number || '-'}</p>
                  </div>
                  {selectedAnimal.birth_date && (
                    <div>
                      <p className="text-muted-foreground">Birth Date</p>
                      <p className="font-medium">{new Date(selectedAnimal.birth_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <p className="font-medium capitalize">{selectedAnimal.status}</p>
                  </div>
                </div>

                {selectedAnimal.notes && (
                  <div className="p-3 rounded-lg bg-muted/30 text-sm">
                    <p className="text-muted-foreground text-xs mb-1">Notes</p>
                    <p>{selectedAnimal.notes}</p>
                  </div>
                )}

                {/* Multi-photo gallery */}
                <AnimalPhotoGallery
                  animalId={selectedAnimal.id}
                  animalKind="individual"
                  animalName={selectedAnimal.name}
                  animalType={selectedAnimal.animal_type || animalType}
                />

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => { setShowDetailDialog(false); openEditDialog(selectedAnimal); }}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowEventDialog(true)}
                    data-testid="add-event-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Log Event
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="ml-auto text-destructive hover:text-destructive"
                    onClick={() => handleDeleteAnimal(selectedAnimal.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Event History */}
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Event History
                  </h4>
                  {!selectedAnimal.events || selectedAnimal.events.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No events recorded yet
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {selectedAnimal.events.map((event) => {
                        const EventIcon = getEventIcon(event.event_type);
                        return (
                          <div 
                            key={event.id}
                            className="flex items-start gap-3 p-2 rounded-lg bg-muted/30 group"
                          >
                            <div className={`p-1.5 rounded ${getEventColor(event.event_type)}`}>
                              <EventIcon className="w-3 h-3" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium capitalize">{event.event_type}</span>
                                {event.value && (
                                  <span className="text-xs text-muted-foreground">
                                    {event.value} {event.unit}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm truncate">{event.description}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(event.date).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                              onClick={() => handleDeleteEvent(event.id)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Log Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm">Event Type</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {EVENT_TYPES.map((et) => (
                  <button
                    key={et.value}
                    type="button"
                    className={`p-2 rounded-lg border text-xs text-center transition-colors ${
                      eventForm.event_type === et.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setEventForm({ ...eventForm, event_type: et.value })}
                  >
                    <et.icon className="w-4 h-4 mx-auto mb-1" />
                    {et.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Description *</Label>
              <Input
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="What happened?"
                className="h-9"
                data-testid="event-description-input"
              />
            </div>

            {(eventForm.event_type === 'weight' || eventForm.event_type === 'production') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Value</Label>
                  <Input
                    type="number"
                    value={eventForm.value}
                    onChange={(e) => setEventForm({ ...eventForm, value: e.target.value })}
                    placeholder="0"
                    className="h-9"
                    data-testid="event-value-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">Unit</Label>
                  <Input
                    value={eventForm.unit}
                    onChange={(e) => setEventForm({ ...eventForm, unit: e.target.value })}
                    placeholder="lbs, eggs, gallons..."
                    className="h-9"
                    data-testid="event-unit-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm">Date</Label>
              <Input
                type="date"
                value={eventForm.date}
                onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                className="h-9"
                data-testid="event-date-input"
              />
            </div>

            <div>
              <Label className="text-sm">Additional Notes</Label>
              <textarea
                className="w-full min-h-[50px] px-3 py-2 rounded-md border border-input bg-background resize-none text-sm"
                value={eventForm.notes}
                onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
                placeholder="Optional details..."
                data-testid="event-notes-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowEventDialog(false); resetEventForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleAddEvent}
                disabled={!eventForm.description}
                data-testid="save-event-btn"
              >
                Log Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IndividualAnimalTracker;
